import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { Reveal } from "@/components/motion";

export const TOKEN_KEY = "hp_admin_token";
const API = process.env.REACT_APP_BACKEND_URL;

export const useAdminToken = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const save = (t) => {
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };
  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };
  return { token, save, signOut };
};

export const adminFetch = (token, url, opts = {}) =>
  fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });

export const AdminLock = ({ onUnlock, blurb }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Sign-in failed");
      onUnlock(data.token);
      setPassword("");
    } catch (e) {
      setError(e.message || "Sign-in failed");
    }
    setBusy(false);
  };

  return (
    <section data-testid="admin-lock-page" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto max-w-md px-5 py-20 md:py-28">
        <Reveal y={12}>
          <div className="border border-border bg-white p-8" data-testid="admin-lock">
            <Lock size={22} className="text-blueprint" />
            <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-ink">Owner access</h1>
            <p className="mt-2 text-sm leading-relaxed text-ink/60">
              {blurb || "This page is private. Enter the owner password to continue."}
            </p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && signIn()}
              placeholder="Owner password"
              data-testid="admin-password-input"
              className="mt-6 w-full border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
            />
            {error && (
              <p className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-auth-error">{error}</p>
            )}
            <button
              onClick={signIn}
              disabled={busy || !password}
              data-testid="admin-login-button"
              className="mt-4 flex w-full items-center justify-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
              {busy ? "Checking…" : "Unlock"}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
